import {Page} from "../page";

export const scheduledGames: Page = {
    uri: 'learn/scheduled-games',
    icon: '🎯',
    title: $localize`:@@page-scheduled-games-title:Scheduled Games`,
    seoTitle: $localize`:@@page-scheduled-games-seo-title:Scheduled games — open tables with seats, teachers and waitlists`,
    summary: $localize`:@@page-scheduled-games-summary:"Looking for 3 players for Gloomhaven on Saturday" — a table for one specific game, with seats, a waitlist, and someone flagged to teach it.`,
    markdownContent: [
        $localize`:@@page-scheduled-games-creating-a-scheduled-game:A scheduled game is a special kind of [event](/learn/events) focused on playing one specific board game. It has its own page, time slot, seat count, roles, and participant list. Scheduled games can stand alone ("Looking for 3 players for Gloomhaven this Saturday") or be nested inside a parent event as part of a larger gathering.

## Creating a Scheduled Game

### Within an Event
1. Go to your event page
2. Choose **Schedule game**
3. Select the **game**
4. Set the **date/time** (it can differ from the parent event's start time)
5. Set **Slots** (total seats) and optionally **Min. players**, so the table shows how many more are needed — and, if you like, **Cancel automatically if the minimum isn't reached** (see [Automatic Cancellation](#page-scheduled-games-auto-cancel) below)
6. Optionally name a **Demonstrator** — someone who'll teach the game — or a **Game Master**. One or the other, not both
7. Set the estimated **duration**
8. Save

### Standalone
Tap the **+** button and choose **Schedule game** to create one without a parent event. It gets its own event page with the same features — RSVP, waitlist, calendar export, social thread.`,

        $localize`:@@page-scheduled-games-help-me-choose:### Not Sure What to Schedule?

The first entry in the game list is **Can't decide?** — *Deal me a game from the table's shelves*. Give it the player count, an age if needed, and a duration and complexity to lean towards, and it deals suggestions from your collection, your game lists and the collections of anyone you have already added — one card at a time until you say **This one!**. The full walkthrough is in [Play Logging](/learn/play-logging).`,

        $localize`:@@page-scheduled-games-seats-roles-waitlist:## Seats, Roles & Waitlist

### Seat Management
Each scheduled game has a fixed seat count. As players join, available seats count down. When full, new players join the waitlist and are notified if a spot opens.

### Demonstrator or Game Master
The organizer can name **one** person to fill a special seat at the table:

- **Demonstrator** — someone who knows the game and will teach it
- **Game Master** — for RPGs and anything needing a dedicated narrator or referee

It's one or the other, and the organizer assigns it — players don't pick a role when they join. Naming a demonstrator is the single most useful thing you can do for a heavy game: it tells newcomers somebody will walk them through it.

### Experience Levels
Each player says where *they* stand when they join, and it shows as a chip on their row:

- **No experience** — never played it
- **Learner** — knows the basics
- **Regular** — plays it comfortably
- **Expert** — knows it inside out

So it describes who's actually at the table, rather than setting a bar for entry. Anyone can change theirs later with **Set experience**. For an organizer it's the quickest way to spot a table full of first-timers that could use a teacher.

![A full table: the Game Master running it, and participants with experience chips and reserved seats](/assets/screenshots/scheduled-games-participants.webp)`,

        $localize`:@@page-scheduled-games-auto-cancel:## Automatic Cancellation
On the [${'Player'} plan](/premium) (on a club's table, the club's plan), a table can call itself off when it stays short of players, so nobody travels to a game that won't happen. In the **Who** step, tick **Cancel automatically if the minimum isn't reached** and pick **How long before the start**: 2, 4, 6 or 12 hours, 1 day or 2 days.

The checkbox only appears when the table has everything it needs to decide:

- a **date and start time** — and only the windows still ahead of the start are offered, so a table starting in under two hours has none
- a **minimum**: your **Min. players**, or else the game's own minimum player count
- sign-ups through Ludoya (the **RSVP** attendance mode). Tournaments don't offer it

Only **Going** players and the seats reserved for them (guests included) count; a **Maybe** or someone on the waitlist doesn't. At twice the chosen window you get a heads-up while there's still time to recruit. If the table is still short when the window arrives, it is canceled and everyone signed up is notified. You can restore it afterwards, which turns automatic cancellation off for that table.`,

        $localize`:@@page-scheduled-games-the-scheduled-game-page:## The Scheduled Game Page

![A scheduled game: who is teaching it, the participants with their experience, and the wait list](/assets/screenshots/scheduled-games-page.webp)

Each scheduled game gets its own dedicated page showing:

- **Game name and box art**
- **Date, time, and estimated duration**
- **Seat status** — filled seats vs. available, broken down by role
- **Experience level indicator**
- **Participant list** with roles assigned
- **Join button** — or **Join wait list** once every seat is taken
- **Bring friends** — once you're in, one button holds **Reserve places** (seats kept for friends, with or without a Ludoya account), **Invite** and **Share join link**
- **Calendar Export** — iCalendar or Google Calendar
- **Social thread** — questions, discussion, and comments specific to this game
- **Players chat** — a [chat](/learn/messaging) for the people at the table, under the participant list. Players join it when they take a seat and leave it if they drop out

If the scheduled game is part of a parent event, a link back to the parent event page is shown.

When it's time to play, **Start** opens the [live table](/learn/live-sessions) with everyone who said they were coming already seated.

On a game still to come that you don't run, **Show me around**, the last line of its ⋮ menu, walks you through the page: the event it's part of, where and when, a seat or the wait list, the game, who teaches it, a clash with another game you joined, and who's playing. The first few times you open a scheduled game, a small bar offers the same tips.`,

        $localize`:@@page-scheduled-games-curating-the-lineup:## Curating the Lineup

For events with multiple scheduled games, the lineup builds as games are added. Organizers can:

- Review the [Event Games](/learn/events) list to see what attendees want to play
- Add scheduled games based on demand
- Balance popular titles with variety
- Stagger start times so players can join multiple games throughout the event`,

        $localize`:@@page-scheduled-games-related:## Related

- [Events](/learn/events) — discovering, joining, creating events, and tournament scoring
- [Event Operations](/learn/event-operations) — QR check-in, floor plans, custom forms
- [Live Sessions](/learn/live-sessions) — real-time play tracking at the table
- [Game Kits](/learn/game-kits) — score sheets, setup guides, and gameplay companions
- [Groups](/learn/groups) — community management
- [Premium](/premium) — ${'Player'} plan for recurring events, ${'Club'} plan for tournaments`,
    ].join('\n\n---\n\n'),
};
